import React, {
    createRef,
    MouseEventHandler, TouchEventHandler,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import * as THREE from "three";
import {OrthographicCamera} from "three";
import {Canvas, CanvasContext, useFrame, useThree} from "react-three-fiber";
import * as VisualizerShader from "./shaders/VisualizerShader";
import * as BackgroundShader from "./shaders/BackgroundShader";
import {CBTextureLoadError, destroyAllCanvases} from "./CBTextureLoader";
import {CBMouseEvent, CBTouchEvent, CBUserTouchEvent} from "./CBTouch";
import {BrowserProperties, WebClientInfo} from "react-client-info";
import {Zoomable, ZoomableState} from "react-zoomable-elements";
import DatGui, {DatBoolean, DatButton, DatFolder, DatNumber} from "react-dat-gui";
import {CBMaterial, CBMaterialProperties} from "./CBMaterial";
import {CBHistoryState, CBToolMode, SuperpixelData} from "./CBTypes";
import {CBMask, CBScene, CBSceneProperties} from "./CBScene";
import {disposeRenderer, disposeScene, safelyTimeout} from "./CBSceneUtils";
import polyfill from '@juggle/resize-observer'

console.log(`react-home-harmony version 0.0.755 by cambrian.io`);

const pattern = /#include <(.*)>/gm;

function parseIncludes( string:any ){
    function replace( match:any , include:any ){
        const replace = THREE.ShaderChunk[ include ];
        return parseIncludes( replace )
    }
    return string.replace( pattern, replace )
}

function insertIntoShader(shader:string, uniqueMatch:string, insertString:string, before:boolean) {
    if (before) {
        return shader.replace(uniqueMatch, insertString + "\n" + uniqueMatch)
    } else {
        return shader.replace(uniqueMatch, uniqueMatch + "\n" + insertString + "\n")
    }
}

let VisualizerMaterialUrl: string | null;
export { VisualizerMaterialUrl };

const DEFAULT_LIGHTING_OFFSET = 0.25;

export class CBAPI {
    public setMaterial:any;
    public setFloorRotation:any
}

const cbapi = new CBAPI();
(window as any).cb = cbapi;

export declare type CBMethods = {
    captureScreenshot: () => Promise<string>

    undoLast:()=>boolean
    restoreAll:()=>boolean
    clearHistory:()=>boolean
    getFloorCenter:()=>[number,number,number]
}

export type CBSceneData = {
    renderCanvas: HTMLCanvasElement
    maskCanvas: HTMLCanvasElement
}

//fix bug in threejs within Safari
if (!(window as any).ResizeObserver) {
    (window as any).ResizeObserver = polyfill
}

export type CBSceneParams = {
    scene?: CBSceneProperties | null | undefined
    onClick?:()=>void
    onMaskClick?:()=>void
    onAssetClick?:()=>void
    fov: number
    cameraPosition: [number, number, number]
    cameraRotation: [number, number, number]
    floorRotation?: number,
    lightingOffset?: number,

    //optional
    material?:CBMaterialProperties | null | undefined
    showControls?: boolean
    toolMode?: CBToolMode
    blendEdges?:boolean
    undoHistorySize?: number
    canLoad?: boolean
    onMaterialTextureChanged?: ((path: string) => void) | null

    onTextureError?: (error: CBTextureLoadError) => void
    onHistoryChanged?: (length: number) => void
    floorTransparency?: number
    onDrawingStart?: (mode: CBToolMode) => void
    onDrawingEnd?: (mode: CBToolMode) => void

    // Called when the mask canvas itself changed (ie. the instance, not what is on it)
    onSceneRender?: (data: CBSceneData) => void
    onSceneLoaded?: (data: CBSceneData, methods:CBMethods) => void

    floorPosition?:number[]
    floorPositionUpdated?:(position:number[])=>void
    floorRotationUpdated?:(rotation:number)=>void
    floorSize?:number[]
}

type CBSceneParamsPrivate = {
    // Called when the mask canvas itself changed (ie. the instance, not what is on it)
    logo:React.RefObject<HTMLElement>
    context?:CanvasContext
    client:BrowserProperties
} & CBSceneParams

function CBSceneRenderer(props: CBSceneParamsPrivate) {
    const { size, gl, camera, scene, invalidate } = useThree();

    // Get the perspective camera to set fov later as the cropping changes.
    const perspectiveCamera = camera as THREE.PerspectiveCamera;

    const [sceneTextures, setSceneTextures]  = useState<CBScene | null>(null);
    const [material, setMaterial] = useState<CBMaterial | null>(null);

    const [backgroundPlanePosition, setBackgroundPlanePosition] = useState<THREE.Vector3>(new THREE.Vector3(0, 0, 0));
    const [backgroundPlaneRotation, setBackgroundPlaneRotation] = useState<THREE.Euler>(new THREE.Euler(0, 0, 0));

    const backgroundSize = useMemo(() => {
        return sceneTextures && sceneTextures.backgroundTexture ? [sceneTextures.width, sceneTextures.height] : [1024, 1024];
    }, [sceneTextures]);

    const [undoHistory] = useState<Array<CBHistoryState>>([]);
    const [drawing, setDrawing] = useState<boolean>(false);
    const [drawingCircleVisible, setDrawingCircleVisible] = useState<boolean>(false);
    const [previousDrawPos, setPreviousDrawPos] = useState<[number, number] | null>(null);
    const maskCanvas = useRef<HTMLCanvasElement>();
    const hasSceneLoaded = useRef(false);
    const [backgroundOffset, setBackgroundOffset] = useState<[number, number]>([0,0]);

    //external methods
    const needsScreenshot = useRef<boolean>(false);
    const screenshot = useRef<string|undefined>(undefined);

    const floor = useRef<THREE.Mesh>();
    const shadowSurface = useRef<THREE.Mesh>();
    const toolMode = props.toolMode;

    const isDrawingTool = useMemo(()=>{
        return toolMode === CBToolMode.Erase || toolMode === CBToolMode.Draw
    },[toolMode]);

    const raycaster = new THREE.Raycaster();

    const captureScreenshot = useCallback(() => {
        return new Promise<string>((resolve, reject) => {
            screenshot.current = undefined;
            needsScreenshot.current = true;
            window.setTimeout(()=>{
                if (screenshot.current) {
                    resolve(screenshot.current)
                } else {
                    reject()
                }
            }, 300);
            invalidate()
        });
    }, []);

    const maskChanged = useCallback((mask:CBMask) => {
        maskCanvas.current = mask.memoryCanvas
    }, []);

    // Make the floor transparent when in edit mode
    const floorTransparency = useMemo(() => {
        return isDrawingTool ? 0.2 : 0
    }, [isDrawingTool]);

    const blendEdges = useMemo(() => {
        return !!props.blendEdges
    }, [props.blendEdges]);

    //add and keep track of listeners, because they need to be replaced
    const listeners = useRef<{[listener: string]: any}>({});

    const addExclusiveEventListener = useCallback((type: string, listener: any) => {
        const existing = listeners.current[type];
        if (existing) {
            gl.domElement.removeEventListener(type, existing)
        }
        gl.domElement.addEventListener(type, listener);
        listeners.current[type] = listener
    }, [gl.domElement]);

    const removeAllEventListeners = useCallback(() => {
        for (let type in listeners) {
            const listener  = listeners.current[type];
            gl.domElement.removeEventListener(type, listener)
        }
        listeners.current = []
    }, [gl.domElement]);

    const disposeAll = useCallback(() => {

        if (material) {
            material.dispose()
        }

        if (sceneTextures) {
            sceneTextures.dispose()
        }

        if (scene) {
            disposeScene(scene)
        }

        if (gl) {
            disposeRenderer(gl)
        }

        destroyAllCanvases();

        removeAllEventListeners()

    }, [gl, material, removeAllEventListeners, scene, sceneTextures]);

    const _disposeRef = useRef(disposeAll);
    useEffect(() => { _disposeRef.current = disposeAll; }, [disposeAll]);

    const _isMounted = useRef(true);

    useEffect(() => {
        _isMounted.current = true;
        return () => {
            _disposeRef.current();
            _isMounted.current = false
        }
    }, []);

    useEffect(() => {
        if (!_isMounted.current) return;
        setPreviousDrawPos(null);
        Zoomable(gl.domElement, props.client, { onZoomChanged: onZoomChanged, canPan: toolMode === CBToolMode.Select})
    }, [gl.domElement, toolMode]);

    const zoomState = useRef<ZoomableState>();

    function onZoomChanged(state: ZoomableState) {
        zoomState.current = state
    }

    const restoreState = useCallback((history?: CBHistoryState) => {
        if (!sceneTextures) return;

        if (!history) {
            history = undoHistory.pop()
        }

        const mask = sceneTextures.masks[0];

        if (history) {
            history.restoreInto(mask.memoryCanvas, () => {
                mask.maskTexture.needsUpdate = true
            })
        } else {
            return false
        }

        invalidate();
        return true

    }, [sceneTextures, undoHistory, invalidate]);

    const clearHistory = useCallback(() => {
        if (undoHistory.length) {
            undoHistory.length = 0;
            return true
        }
        return false
    }, [undoHistory]);

    const undoLast = useCallback(() => {
        if (undoHistory.length) {
            restoreState();
            return true
        }
        return false
    }, [restoreState]);

    const restoreAll = useCallback(() => {
        if (undoHistory.length) {
            restoreState(undoHistory[0]);
            clearHistory();
            return true
        }
        return false
    }, [ restoreState, clearHistory]);

    const onHistoryChanged = props.onHistoryChanged;
    useEffect(() => {
        if (onHistoryChanged) {
            onHistoryChanged(undoHistory.length)
        }
    }, [onHistoryChanged, undoHistory.length]);

    useMemo(() => {
        return (x: number, y: number) => {
            if (sceneTextures && sceneTextures.superpixels) {
                const ctx = sceneTextures.superpixels.canvas.getContext("2d");

                if (ctx) {
                    return ctx.getImageData(x * sceneTextures.superpixels.canvas.width, y * sceneTextures.superpixels.canvas.height, 1, 1).data
                }
            }
            return new Uint8ClampedArray(4)
        }
    }, [sceneTextures]);

    const saveUndoState = useCallback(() => {
        if (!_isMounted.current || !sceneTextures || !sceneTextures.masks.length) return;
        const mask = sceneTextures.masks[0];

        maskChanged(mask);

        const currentState = new CBHistoryState(mask.memoryCanvas);
        undoHistory.push(currentState);

        if (undoHistory.length > (props.undoHistorySize ? props.undoHistorySize : 100)) {
            undoHistory.shift();
        }
    }, [sceneTextures, undoHistory, props.undoHistorySize, maskChanged]);

    const getDrawLineWidth = useCallback(() => {
            return sceneTextures && sceneTextures.masks.length ? window.devicePixelRatio * 0.012 * Math.sqrt(Math.pow(sceneTextures.masks[0].memoryCanvas.width, 2)
                + Math.pow(sceneTextures.masks[0].memoryCanvas.height, 2)) : 10},
        [sceneTextures]);

    // Set the callback whenever it changes

    const getSuperpixels = useMemo(() => {
        return (x: number, y: number) => {
            const hitSuperpixelData: SuperpixelData[] = [];
            if (sceneTextures && sceneTextures.superpixels) {
                for (const label in sceneTextures.superpixels.labels) {
                    const labelData = sceneTextures.superpixels.labels[label];
                    if (Math.pow(labelData.center[0] - x, 2) + Math.pow(labelData.center[1] - y, 2) <= Math.pow(0.012/2 + labelData.meanRadius, 2)) {
                        hitSuperpixelData.push(labelData)
                    }
                }
            }
            return hitSuperpixelData
        }
    }, [sceneTextures]);

    const drawOnMask = useCallback((e: any, mask:CBMask, from: [number, number], to: [number, number]) => {
        const ctx = mask.memoryCanvas.getContext("2d");
        if (ctx) {
            if (sceneTextures && sceneTextures.superpixels) {
                // Fill superpixel at start position (from).
                ctx.fillStyle = toolMode === CBToolMode.Draw ? "white" : "black";

                const superpixels = getSuperpixels(from[0], from[1]);

                for (const superpixel of superpixels) {
                    if (superpixel.pixels.length > 0) {
                        for (const pixel of superpixel.pixels) {
                            ctx.fillRect(pixel[0] * mask.memoryCanvas.width, pixel[1] * mask.memoryCanvas.height,
                                Math.ceil(mask.memoryCanvas.width / sceneTextures.superpixels.canvas.width),
                                Math.ceil(mask.memoryCanvas.height / sceneTextures.superpixels.canvas.height))
                        }

                        mask.maskTexture.needsUpdate = true
                    }
                }
            }

            // Update the visual drawing circle's position
            const overlayCircle = document.getElementById('drawing-mark-overlay');
            if (overlayCircle && e.clientX && e.clientY && e.currentTarget) {
                overlayCircle.style.left = `${e.clientX - overlayCircle.clientWidth / 2}px`;
                overlayCircle.style.top = `${e.clientY - overlayCircle.clientHeight / 2}px`
            }
        }
    }, [toolMode, getSuperpixels, sceneTextures]);

    const anisotropy = useMemo(() => {
        return gl ? gl.capabilities.getMaxAnisotropy() : 0
    }, [gl]);

    const viewportSize = useMemo(() => {
        return size ? [window.devicePixelRatio * size.width, window.devicePixelRatio * size.height] : [1, 1]
    }, [size]);

    const onTextureError = props.onTextureError;
    const textureError = useCallback((error: CBTextureLoadError) => {
        console.log(error.message);

        if (onTextureError) {
            onTextureError(error)
        }
    }, [onTextureError]);

    const [loadedPath, setLoadedPath] = useState();

    // Load background texture
    const _isLoading = useRef(false);
    useEffect(() => {

        const context = props.context;

        if (!_isMounted.current
            || !props.scene || !props.scene.backgroundUrl
            || !props.canLoad || _isLoading.current || !context) {
            //console.log(`scene: ${!!props.scene}, canLoad: ${props.canLoad}, _isLoading: ${_isLoading.current}, context: ${!!context}`)
            return
        }
        if (loadedPath === props.scene.backgroundUrl) {
            //same scene, nothing to do
            return;
        }

        setLoadedPath(props.scene.backgroundUrl);

        _isLoading.current = true;



        //console.log(`LOADING SCENE: ${props.scene.backgroundUrl}`);

        const scene = new CBScene(props.scene, context);

        scene.load(() => {
            if (!_isMounted.current) return;
            _isLoading.current = false;

            if (sceneTextures) {
                sceneTextures.dispose();
            }

            setSceneTextures(scene);
            maskChanged(scene.masks[0]);

            safelyTimeout(()=>{
                hasSceneLoaded.current = false;
                invalidate()
            }, 200)
        }, (error)=>{
            _isLoading.current = false;
            //console.log(`LOADING SCENE FAILED: ${error.message}`)
            textureError(error)
        })

    }, [props.canLoad, props.scene, textureError, props.context, loadedPath, sceneTextures]);

    // Call callback when material texture changes
    const onMaterialTextureChanged = props.onMaterialTextureChanged;
    const materialLoaded = useCallback((properties:CBMaterialProperties) => {
        if (!_isMounted.current) return;
        VisualizerMaterialUrl = properties.diffuseUrl;
        if (onMaterialTextureChanged && VisualizerMaterialUrl) {
            onMaterialTextureChanged(properties.diffuseUrl)
        }
    }, [onMaterialTextureChanged]);

    const setMaterialTextureProperties = useCallback((texture:THREE.Texture) => {
        if (!_isMounted.current) return;
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.anisotropy = anisotropy;
    }, [anisotropy]);

    const createNewMaterial = useCallback((mProps:CBMaterialProperties, context:CanvasContext, callback:(mat:CBMaterial)=>void, error:(error: CBTextureLoadError)=>void) => {
        if (!_isMounted.current) return;
        const mat = new CBMaterial(mProps, "floor", context);

        mat.load(() => {
            if (!_isMounted.current) return;

            if (mat.diffuseTexture) {
                setMaterialTextureProperties(mat.diffuseTexture)
            }
            if (mat.normalsTexture && mat.specularTexture) {
                setMaterialTextureProperties(mat.normalsTexture);
                setMaterialTextureProperties(mat.specularTexture)
            }

            callback(mat)

        },  error)

    }, [setMaterialTextureProperties, textureError]);

    const [diffuseLastLoad, setDiffuseLastLoad] = useState();

    // Load material texture
    useMemo(() => {
        if (!_isMounted.current || !props.material || !props.context) return;

        if ((material && props.material.diffuseUrl === material.props.diffuseUrl)
            || props.material.diffuseUrl === diffuseLastLoad) return;

        setDiffuseLastLoad(props.material.diffuseUrl);

        createNewMaterial(props.material, props.context, (mat:CBMaterial) => {
            if (!_isMounted.current) return;
            if (material) {
                material.dispose();
            }
            setMaterial(mat);
            materialLoaded(mat.props)
        }, textureError)

    }, [createNewMaterial, material, materialLoaded, props.material, props.context]);

    const alignBackgroundPlaneWithCamera = useCallback(() => {
        // Place the plane in front of the camera facing it.
        if (!_isMounted.current) return;

        let cameraForward = new THREE.Vector3(0, 0, 1);
        cameraForward = cameraForward.applyQuaternion(camera.quaternion);

        const cameraPos = new THREE.Vector3();
        camera.getWorldPosition(cameraPos);

        const planePosition = cameraPos.addScaledVector(cameraForward, -100);
        const planeRotation = new THREE.Euler().setFromRotationMatrix(camera.matrix);

        if (_isMounted.current) {
            setBackgroundPlanePosition(planePosition);
            setBackgroundPlaneRotation(planeRotation)
        }

    }, [camera]);

    // Update elevation on camera, doesn't bind for some reason
    useEffect(() => {
        perspectiveCamera.position.x = props.cameraPosition[0];
        perspectiveCamera.position.y = props.cameraPosition[1];
        perspectiveCamera.position.z = props.cameraPosition[2];

        alignBackgroundPlaneWithCamera()
    }, [props.cameraPosition, perspectiveCamera, alignBackgroundPlaneWithCamera]);

    useEffect(() => {
        const rotation = new THREE.Euler(props.cameraRotation[0], props.cameraRotation[1], props.cameraRotation[2]);
        perspectiveCamera.setRotationFromEuler(rotation);
        alignBackgroundPlaneWithCamera()
    }, [props.cameraRotation, perspectiveCamera, alignBackgroundPlaneWithCamera]);


    useEffect(() => {
        if (!_isMounted.current || !sceneTextures) return;

        // Calculate the width after cropping.
        // Obtained by taking the offsetX in shader, multiplying by 2, multiplying by background width.
        const scale = Math.min(backgroundSize[0] / viewportSize[0], backgroundSize[1] / viewportSize[1]);
        const newSize = [backgroundSize[0] / scale, backgroundSize[1] / scale];
        const relativeCrop = [(newSize[0] - viewportSize[0]) / newSize[0], (newSize[1] - viewportSize[1]) / newSize[1]];

        const croppedWidth = (1 - relativeCrop[0]) * backgroundSize[0];
        const croppedHeight = (1 - relativeCrop[1]) * backgroundSize[1];

        if (props.scene && props.scene.anchorPoint && !props.showControls) {
            const vpAR = viewportSize[0] / viewportSize[1];
            const iAR = backgroundSize[0] / backgroundSize[1];

            let offset:[number,number] = [0,0];

            if (iAR > vpAR) {
                //cropped horizontally
                const cw = viewportSize[0] * scale;
                offset[0] = (props.scene.anchorPoint[0] - 0.5) * (backgroundSize[0] - cw) / backgroundSize[0];
            } else {
                //cropped vertically
                const ch = viewportSize[1] * scale;
                offset[1] = (0.5 - props.scene.anchorPoint[1]) * (backgroundSize[1] - ch) / backgroundSize[1];
            }

            //let full_size = [backgroundSize[0] / scale, backgroundSize[1] / scale];

            // perspectiveCamera.setViewOffset(
            //     viewportSize[0],
            //     viewportSize[1],
            //     viewportSize[0] * offset[0],
            //     viewportSize[1] * offset[1],
            //     full_size[0],
            //     full_size[1]
            // );

            setBackgroundOffset(offset)
        }

        // Calculate the horizontal fov after cropping using formulas from the pinhole camera model.
        const croppedFov = 180 / Math.PI * 2 * Math.atan((croppedWidth / 2) / (0.5 * backgroundSize[0] / Math.tan(Math.PI / 180 * props.fov * 0.5)));

        // We need to manually set fov. For some reason the binding does not work correctly.
        // ThreeJS expects vertical fov, our fov is horizontal however, so we need to convert it
        perspectiveCamera.fov = 180 / Math.PI * 2 * Math.atan(Math.tan(Math.PI / 180 * croppedFov / 2) * croppedHeight / croppedWidth);

        perspectiveCamera.updateProjectionMatrix()

    }, [sceneTextures, viewportSize, props.fov, perspectiveCamera, backgroundSize, props.scene, props.showControls]);

    const materialArgs = useMemo(() => {
        // Calculate dpi of the material after threejs' resizing
        const materialTextureSize = material && material.diffuseTexture ? [material.width, material.height] : [1024, 1024];
        const imageDpi = material ? [material.props.ppi, material.props.ppi] : [20,20];
        const planeSize = props.floorSize ? props.floorSize : [1000, 1000];
        const hasPBR = material ? !!material.normalsTexture && !!material.specularTexture : false;

        const customUniforms = {
            diffuse: { type: "vec3", value: [0,0,0]},
            emissive: { type: "vec3", value: [0.0,0,0]},
            specular: { type: "vec3", value: [1.0,0.9,1.0]},
            shininess: { type: "float", value: 1.0},
            opacity: { type: "float", value: 1.0},

            map: { type: "sampler2D", value: material ? material.diffuseTexture : null },
            normalMap: { type: "sampler2D", value: material ? material.normalsTexture : null },
            specularMap: { type: "sampler2D", value: material ? material.specularTexture : null },
            imageSize: { type: "vec2", value: materialTextureSize },
            planeSize: { type: "vec2", value: planeSize },
            imageDpi: { type: "vec2", value: imageDpi },
            maskSampler: { type: "sampler2D", value: sceneTextures && sceneTextures.masks.length ? sceneTextures.masks[0].maskTexture : null},
            lightingSampler: { type: "sampler2D", value: sceneTextures ? sceneTextures.lightingTexture : null},
            lightingOffset: { type: "float", value: props.lightingOffset !== undefined ? props.lightingOffset : DEFAULT_LIGHTING_OFFSET},
            backgroundSize: { type: "vec2", value: backgroundSize },
            backgroundOffset: { type: "vec2", value: backgroundOffset },
            viewportSize: { type: "vec2", value: viewportSize },
            transparency: { type: "float", value: floorTransparency },
            blendEdges: { type: "bool", value: blendEdges },
            isPBR: { type: "bool", value: hasPBR }
        };

        const uniforms = Object.assign(THREE.ShaderLib.phong.uniforms, Object.assign(THREE.UniformsLib['lights']), customUniforms);

        return {
            fragmentShader: VisualizerShader.fragmentShader,
            vertexShader: VisualizerShader.vertexShader,
            uniforms:uniforms
        }
    }, [material, props.floorSize, sceneTextures, backgroundSize, backgroundOffset, viewportSize, floorTransparency, blendEdges]);

    const backgroundMaterialArgs = useMemo(() => {
        return {
            fragmentShader: BackgroundShader.fragmentShader,
            vertexShader: BackgroundShader.vertexShader,
            uniforms: {
                imageSampler: { type: "sampler2D", value: sceneTextures ? sceneTextures.backgroundTexture : null },
                imageSize: { type: "vec2", value: backgroundSize },
                imageOffset: { type: "vec2", value: backgroundOffset },
                viewportSize: { type: "vec2", value: viewportSize }
            }
        }
    }, [backgroundSize, backgroundOffset, sceneTextures, viewportSize]);

    const canvasToMaskCoordinates = useMemo(() => {
        return (coords: [number, number]) => {
            const scale = Math.min(backgroundSize[0] / viewportSize[0], backgroundSize[1] / viewportSize[1]);
            const newSize = [backgroundSize[0] / scale, backgroundSize[1] / scale];
            const relativeCrop = [(newSize[0] - viewportSize[0]) / newSize[0], (newSize[1] - viewportSize[1]) / newSize[1]];

            const maskCoords: [number, number] = [relativeCrop[0] / 2 + coords[0] * (1 - relativeCrop[0]), relativeCrop[1] / 2 + coords[1] * (1 - relativeCrop[1])];

            return maskCoords
        }
    }, [backgroundSize, viewportSize]);

    const getMaskCoords = useMemo(() => {
        return (e: CBUserTouchEvent) => {
            if (e.currentTarget && e.currentTarget.parentElement) {

                let canvasWidth = window.devicePixelRatio * gl.domElement.clientWidth;
                let canvasHeight = window.devicePixelRatio * gl.domElement.clientHeight;

                const rect = e.currentTarget.parentElement.getBoundingClientRect();
                let offsetLeft = rect.left;
                let offsetTop = rect.top;

                //take into account all modifications by zoom and pan
                if (zoomState.current) {
                    canvasWidth *= zoomState.current.scale;
                    canvasHeight *= zoomState.current.scale;

                    offsetLeft += zoomState.current.posX - (zoomState.current.scale - 1) * (gl.domElement.clientWidth) / 2.0;
                    offsetTop += zoomState.current.posY - (zoomState.current.scale - 1) * (gl.domElement.clientHeight) / 2.0
                }

                const x = e.clientX - offsetLeft;
                const y = e.clientY - offsetTop;
                const canvasRelX = window.devicePixelRatio * x / canvasWidth;
                const canvasRelY = window.devicePixelRatio * y / canvasHeight;

                return canvasToMaskCoordinates([canvasRelX, canvasRelY])
            }

            return null
        }
    }, [gl.domElement.clientWidth, gl.domElement.clientHeight, canvasToMaskCoordinates]);

    const get3DIntersection = useCallback((coords: [number, number], objects:THREE.Object3D[])=>{
        const xPos = coords[0] * 2 - 1;
        const yPos = coords[1] * -2 + 1;
        raycaster.setFromCamera( { x: xPos, y: yPos}, camera);
        const intersections = raycaster.intersectObjects( objects, true );

        if (intersections.length) {
            return intersections[0];
        }
    }, [camera, raycaster]);

    useEffect(() => {
        if (!_isMounted.current) return;
        const overlayCircle = document.getElementById('drawing-mark-overlay');
        if (overlayCircle) {
            overlayCircle.style.display = drawingCircleVisible ? 'block' : 'none';
            gl.domElement.style.cursor = drawingCircleVisible ? 'none' : 'default'
        }

    }, [drawingCircleVisible, gl.domElement.style.cursor]);

    const onDrawingStart = props.onDrawingStart;
    const onDrawingEnd = props.onDrawingEnd;
    useEffect(() => {
        if (!_isMounted.current) return;

        const md = toolMode ? toolMode : CBToolMode.Select;
        if (drawing) {
            if (onDrawingStart) {
                onDrawingStart(md)
            }
        } else if (_isMounted.current) {
            setPreviousDrawPos(null);

            if (onDrawingEnd) {
                onDrawingEnd(md)
            }
        }
    }, [drawing, onDrawingEnd, onDrawingStart, toolMode]);

    const [dragging, setIsDragging] = useState(false);
    const draggingStart = useRef<THREE.Vector3>();
    const draggingStartAngle = useRef(0);
    const draggingObjectStart = useRef<THREE.Vector3>();

    const isMaskedAtPoint = useCallback((coords:[number, number])=>{
        if (!maskCanvas.current) return false;
        const ctx = maskCanvas.current.getContext("2d");
        if (!ctx) return false;

        const xPos = coords[0] * maskCanvas.current.width;
        const yPos = coords[1] * maskCanvas.current.height;

        const pixel = ctx.getImageData(xPos, yPos, 1, 1);

        const isMasked = pixel.data.length > 0 && pixel.data[0] === 255;

        return isMasked;

    }, [maskCanvas]);

    const [floorPosition, setFloorPosition] = useState(props.floorPosition);
    const [floorRotation, setFloorRotation] = useState(props.floorRotation);

    //sync when not dragging:
    const handleDrag = useCallback((event: CBUserTouchEvent)=>{
        const coords = getMaskCoords(event);
        if (!coords || !shadowSurface.current) return;
        const intersection = get3DIntersection(coords, [shadowSurface.current]);

        if (intersection && draggingStart.current && draggingObjectStart.current) {
            const startPoint = draggingStart.current;

            if (toolMode === CBToolMode.Translate) {
                const offset = new THREE.Vector3(
                    intersection.point.x - startPoint.x,
                    intersection.point.y - startPoint.y,
                    intersection.point.z - startPoint.z);
                const newPos = [draggingObjectStart.current.x + offset.x, draggingObjectStart.current.y, draggingObjectStart.current.z + offset.z];
                setFloorPosition(newPos)
            } else {

                const vectorA = new THREE.Vector3(
                    startPoint.x - draggingObjectStart.current.x,
                    startPoint.y - draggingObjectStart.current.y,
                    startPoint.z - draggingObjectStart.current.z).normalize();

                const vectorB = new THREE.Vector3(
                    intersection.point.x - draggingObjectStart.current.x,
                    intersection.point.y - draggingObjectStart.current.y,
                    intersection.point.z - draggingObjectStart.current.z).normalize();

                let angle = Math.acos(vectorA.dot(vectorB));
                const cross = vectorA.cross(vectorB);

                const planeNormal = new THREE.Vector3(0,1,0);
                if (planeNormal.dot(cross) < 0) { // Or > 0
                    angle = -angle;
                }
                setFloorRotation(angle + draggingStartAngle.current)
            }
        }

    }, [get3DIntersection, draggingStart, draggingObjectStart, draggingStartAngle]);

    const floorPos = props.floorPosition;
    const floorRot = props.floorRotation;

    useEffect(()=>{
        if (!dragging) {
            if (floorPos) {
                setFloorPosition(floorPos)
            }
            if (floorRot) {
                setFloorRotation(floorRot)
            }
        }
    }, [dragging, floorPos, floorRot, camera]);

    const onDragStart = useCallback(() => {
        if (!_isMounted.current) return;
        setIsDragging(true)
    }, [floorPos]);

    const floorPositionUpdated = props.floorPositionUpdated;
    const floorRotationUpdated = props.floorRotationUpdated;
    const onDragEnd = useCallback(() => {
        if (!_isMounted.current) return;
        if (toolMode === CBToolMode.Translate && floorPositionUpdated && floorPosition) {
            floorPositionUpdated(floorPosition)
        }
        if (toolMode === CBToolMode.Rotate && floorRotationUpdated && floorRotation) {
            floorRotationUpdated(floorRotation)
        }
        setIsDragging(false)
    }, [dragging, toolMode, floorPosition, floorPositionUpdated, floorRotationUpdated, floorRotation]);

    const onUserTouchUp = useCallback(() => {
        if (!_isMounted.current) return;
        setDrawing(false);
        if (dragging) {
            onDragEnd()
        }
    }, [setDrawing, dragging, onDragEnd]);

    const onUserTouchOut = useCallback(() => {
        if (!_isMounted.current) return;
        setDrawing(false);
        setDrawingCircleVisible(false);
        if (dragging) {
            onDragEnd()
        }
    }, [setDrawing, dragging, onDragEnd]);

    const onUserTouchMove = useCallback((e: CBUserTouchEvent) => {
        if (!_isMounted.current) return;

        if (dragging) {
            handleDrag(e)
        }
        else if (isDrawingTool) {
            if (drawing && previousDrawPos && sceneTextures && sceneTextures.masks.length) {
                const maskCoords = getMaskCoords(e);
                const mask = sceneTextures.masks[0];
                if (maskCoords) {
                    drawOnMask(e, mask, previousDrawPos, maskCoords);
                    setPreviousDrawPos(maskCoords)
                }
            }

            const overlayCircle = document.getElementById('drawing-mark-overlay');
            if (overlayCircle) {
                const diameter = getDrawLineWidth();

                overlayCircle.style.width = `${diameter}px`;
                overlayCircle.style.height = `${diameter}px`;
                overlayCircle.style.left = `${e.clientX - overlayCircle.clientWidth / 2}px`;
                overlayCircle.style.top = `${e.clientY - overlayCircle.clientHeight / 2}px`
            }
        }

        if (!_isMounted.current) return;
        setDrawingCircleVisible(!props.client.isMobile && isDrawingTool)

    }, [isDrawingTool, drawing, previousDrawPos, isDrawingTool, sceneTextures, getMaskCoords, drawOnMask, getDrawLineWidth, dragging, handleDrag]);

    const onUserTouchDown = useCallback((e: CBUserTouchEvent) => {
        if (!_isMounted.current) return;

        if (props.onClick) {
            props.onClick();
        }

        const drawCoords = getMaskCoords(e);

        if (isDrawingTool) {

            setDrawing(true);
            saveUndoState();
            setPreviousDrawPos(drawCoords);
            onUserTouchMove(e);

            if (drawCoords && sceneTextures && sceneTextures.masks.length) {
                drawOnMask(e, sceneTextures.masks[0], drawCoords, drawCoords)
            }
        }
        else {
            setDrawing(false);
            if (drawCoords) {

                let clickedOnAsset = false;
                if (floor.current) {
                    const intersection = get3DIntersection(drawCoords, [floor.current]);
                    if (intersection) {
                        clickedOnAsset = true;
                        if (props.onAssetClick) {
                            props.onAssetClick();
                        }
                    }
                }

                if (shadowSurface.current && isMaskedAtPoint(drawCoords)) {
                    const intersection = get3DIntersection(drawCoords, [shadowSurface.current]);
                    if (intersection) {
                        draggingStart.current = intersection.point;
                        draggingStartAngle.current = floorRotation ? floorRotation : 0;
                        draggingObjectStart.current = props.floorPosition ? new THREE.Vector3(props.floorPosition[0], props.floorPosition[1], props.floorPosition[2]) : new THREE.Vector3();

                        if ((clickedOnAsset || toolMode === CBToolMode.Translate || toolMode === CBToolMode.Rotate) && !dragging) {
                            onDragStart();
                        }

                        if (props.onMaskClick) {
                            props.onMaskClick();
                        }
                    }
                }

                //const floorIntersection = get3DMaskIntersection();
            }
        }
    }, [isDrawingTool, getMaskCoords, saveUndoState, onUserTouchMove, sceneTextures, drawOnMask, get3DIntersection, shadowSurface, floor,
        dragging, draggingStart, draggingObjectStart, draggingStartAngle, onDragStart, toolMode, props.floorPosition, props.onMaskClick, props.onAssetClick]);

    const onMouseDown = useCallback((e: MouseEvent) => {
        if (!_isMounted.current) return;
        onUserTouchDown(new CBMouseEvent(e))
    }, [onUserTouchDown]);

    const onMouseMove = useCallback((e: MouseEvent) => {
        if (!_isMounted.current) return;
        onUserTouchMove(new CBMouseEvent(e))
    }, [onUserTouchMove]);

    const onMouseUp = useCallback(() => {
        if (!_isMounted.current) return;
        onUserTouchUp()
    }, [onUserTouchUp]);

    const onMouseOut = useCallback(() => {
        if (!_isMounted.current) return;
        onUserTouchOut()
    }, [onUserTouchOut]);

    const onTouchDown = useCallback((e: TouchEvent) => {
        if (!_isMounted.current) return;
        if (e.touches.length === 1) {
            onUserTouchDown(new CBTouchEvent(e))
        }
    }, [onUserTouchDown]);

    const onTouchMove = useCallback((e: TouchEvent) => {
        if (!_isMounted.current) return;
        if (e.touches.length === 1) {
            onUserTouchMove(new CBTouchEvent(e))
        }
    }, [onUserTouchMove]);

    const onTouchUp = useCallback((e: TouchEvent) => {
        if (!_isMounted.current) return;
        if (e.touches.length === 0) {
            onUserTouchUp()
        }
    }, [onUserTouchUp]);

    const onTouchCancel = useCallback((e: TouchEvent) => {
        if (!_isMounted.current) return;
        if (e.touches.length === 1) {
            onUserTouchOut()
        }
    }, [onUserTouchOut]);

    useEffect(() => {
        if (gl.domElement) {
            if (props.client.isMobile) {
                addExclusiveEventListener("touchstart", onTouchDown);
                addExclusiveEventListener("touchmove", onTouchMove);
                addExclusiveEventListener("touchend", onTouchUp);
                addExclusiveEventListener("touchcancel", onTouchCancel)
            } else {
                addExclusiveEventListener("mousedown", onMouseDown);
                addExclusiveEventListener("mousemove", onMouseMove);
                addExclusiveEventListener("mouseup", onMouseUp);
                addExclusiveEventListener("mouseout", onMouseOut)
            }
        }
    }, [addExclusiveEventListener, gl.domElement, onMouseDown, onMouseMove, onMouseOut, onMouseUp, onTouchCancel, onTouchDown, onTouchMove, onTouchUp]);

    useEffect(() => {
        if (!_isMounted.current) return;
        const overlayCircle = document.getElementById('drawing-mark-overlay');
        if (overlayCircle) {
            overlayCircle.style.display = drawingCircleVisible ? 'block' : 'none';
            gl.domElement.style.cursor = drawingCircleVisible ? 'none' : 'default'
        }
    }, [drawingCircleVisible, gl.domElement.style.cursor]);

    const logo = props.logo;
    useEffect(() => {
        if (!_isMounted.current) return;
        if (logo.current) {
            logo.current.style.display = drawingCircleVisible ? "none" : "block"
        }

    }, [drawingCircleVisible, logo]);

    const getFloorCenter = useCallback(()=>{
        let center = new THREE.Vector3();

        if (shadowSurface.current) {
            const intersection = get3DIntersection([0.5,0.7], [shadowSurface.current]);
            if (intersection) {
                center = intersection.point
            }
        }

        return [center.x, 0.0, center.z] as [number,number,number]
    }, [camera]);

    //callback per render
    useFrame(() => {
        if (!_isMounted.current) return;
        gl.render(scene, camera);

        if (needsScreenshot.current) {
            needsScreenshot.current = false;
            screenshot.current = gl.domElement.toDataURL("image/jpeg")
        }

        if (props.onSceneRender && maskCanvas.current) {
            props.onSceneRender({
                maskCanvas : maskCanvas.current,
                renderCanvas: gl.domElement
            })
        }

        if (!hasSceneLoaded.current) {
            hasSceneLoaded.current = true;

            if (props.onSceneLoaded && maskCanvas.current) {
                props.onSceneLoaded({
                    maskCanvas : maskCanvas.current,
                    renderCanvas: gl.domElement
                }, {
                    captureScreenshot:captureScreenshot,
                    undoLast:undoLast,
                    restoreAll:restoreAll,
                    clearHistory:clearHistory,
                    getFloorCenter:getFloorCenter
                })
            }
        }
    });

    const shadowMaterial = useRef<THREE.ShadowMaterial>();
    const [shadowShader, setShadowShader] = useState<THREE.Shader>();

    useEffect(()=>{
        if (shadowShader && shadowMaterial.current) {
            shadowShader.uniforms['maskSampler'].value = sceneTextures && sceneTextures.masks.length ? sceneTextures.masks[0].maskTexture : null;
            shadowShader.uniforms['viewportSize'].value = new THREE.Vector2(viewportSize[0], viewportSize[1]);
            shadowShader.uniforms['backgroundSize'].value = new THREE.Vector2(backgroundSize[0], backgroundSize[1]);
            shadowShader.uniforms['backgroundOffset'].value = new THREE.Vector2(backgroundOffset[0], backgroundOffset[1]);
            shadowMaterial.current.needsUpdate = true
        }
    },[shadowShader, shadowMaterial, viewportSize, backgroundSize, sceneTextures]);

    useEffect(()=>{
        if (shadowMaterial.current) {
            //shadowMaterial.current.blending = THREE.MultiplyBlending
            shadowMaterial.current.onBeforeCompile = (shader) => {
                shader.fragmentShader = parseIncludes(shader.fragmentShader);

                const uniformPlace = "void main() {";
                const uniformReplacement = "" +
                    "uniform sampler2D maskSampler;\n" +
                    "uniform vec2 viewportSize;\n" +
                    "uniform vec2 backgroundSize;\n" +
                    "uniform vec2 backgroundOffset;\n";

                shader.fragmentShader = insertIntoShader(shader.fragmentShader, uniformPlace, uniformReplacement, true);

                const calcPlace = "gl_FragColor = vec4( color, opacity * ( 1.0 - getShadowMask() ) );";
                const calcReplacement = "float scale = min(backgroundSize.x / viewportSize.x, backgroundSize.y / viewportSize.y);\n" +
                    "    vec2 newSize = backgroundSize / scale;\n" +
                    "    vec2 offset = (newSize - viewportSize) / 2.0;\n" +
                    "    vec2 relativeOffset = offset / newSize;\n" +
                    "    \n" +
                    "    vec2 screenCoord = vec2(\n" +
                    "        backgroundOffset.x + relativeOffset.x + (1.0 - 2.0 * relativeOffset.x) * gl_FragCoord.x / viewportSize.x,\n" +
                    "        backgroundOffset.y + relativeOffset.y + (1.0 - 2.0 * relativeOffset.y) * gl_FragCoord.y / viewportSize.y\n" +
                    "    );\n" +
                    "    float mask = texture2D(maskSampler, screenCoord).r;\n" +
                    "    gl_FragColor.a = gl_FragColor.a * mask;";

                shader.fragmentShader = insertIntoShader(shader.fragmentShader, calcPlace, calcReplacement, false);

                //needs all uniforms to compile:
                shader.uniforms['maskSampler'] = {value: sceneTextures && sceneTextures.masks.length ? sceneTextures.masks[0] : new THREE.Texture()};
                shader.uniforms['viewportSize'] = {value: new THREE.Vector2(viewportSize[0], viewportSize[1])};
                shader.uniforms['backgroundSize'] = {value: new THREE.Vector2(backgroundSize[0], backgroundSize[1])};
                shader.uniforms['backgroundOffset'] = {value: new THREE.Vector2(backgroundOffset[0], backgroundOffset[1])};

                setShadowShader(shader)
            };
        }
    }, [shadowMaterial, sceneTextures, viewportSize]);

    const hasShadows = useMemo(()=>{
        return props.floorSize && props.floorSize[0] < 50
    }, [props.floorSize]);

    return (
            <group visible={!_isLoading.current}>
                <mesh position={backgroundPlanePosition} rotation={backgroundPlaneRotation}
                      renderOrder={1000} castShadow={false}
                      geometry={new THREE.PlaneBufferGeometry(1000, 1000)}>
                    <shaderMaterial attach="material" args={[backgroundMaterialArgs]} />
                </mesh>

                <directionalLight castShadow
                                  color={new THREE.Color(1.0,1.0,1.0)}
                                  position={[0, 4, -3]}
                                  intensity={0.2}
                                  shadow-mapSize-width={1024}
                                  shadow-mapSize-height={1024}
                                  shadow-camera={new OrthographicCamera(-3,3,3,-3, 0.01, 100000)} />

                <directionalLight castShadow
                                  color={new THREE.Color(1.0,1.0,1.0)}
                                  position={[0, 4, -3]}
                                  intensity={0.5}
                                  shadow-mapSize-width={1024}
                                  shadow-mapSize-height={1024}
                                  shadow-radius={2}
                                  shadow-camera={new OrthographicCamera(-5,5,5,-5, 0.01, 100000)}/>

                {material && <mesh ref={floor} castShadow={hasShadows}
                      position={floorPosition ? floorPosition : [0,0,0]}
                      rotation={[0, floorRotation ? floorRotation : 0, 0]}>}>
                    <boxBufferGeometry attach="geometry" args={[props.floorSize ? props.floorSize[0] : 1000, 0.015, props.floorSize ? props.floorSize[1] : 1000]} />
                    <shaderMaterial attach="material" args={[materialArgs]} transparent lights />
                </mesh>}

                {material && <mesh ref={shadowSurface} receiveShadow={hasShadows}
                      position={[0, 0, -3]}
                      rotation={[-Math.PI / 2, 0, 0]}
                      geometry={new THREE.PlaneBufferGeometry(10, 10)}>
                    <shadowMaterial ref={shadowMaterial} attach="material" opacity={0.5} />
                </mesh>}
            </group>
    )
}

export function CBVisualizer(props: CBSceneParams) {
    const logo = createRef<HTMLElement>();
    const [context, setContext] = useState<CanvasContext>();
    const [browserProperties, setBrowserProperties] = useState<BrowserProperties>({});

    const [material, setMaterial] = useState<CBMaterialProperties|null>();

    const cRotation = props.cameraRotation;
    const [cameraRotation, setCameraRotation] = useState(props.cameraRotation);
    useEffect(() => {
        if (!_isMounted.current) return;
        setCameraRotation(cRotation)
    }, [cRotation]);

    const fRotation = props.floorRotation;
    const [floorRotation, setFloorRotation] = useState(props.floorRotation);
    useEffect(() => {
        if (!_isMounted.current) return;
        setFloorRotation(fRotation)
    }, [fRotation]);

    const cPosition = props.cameraPosition;
    const [cameraPosition, setCameraPosition] = useState(props.cameraPosition);
    useEffect(() => {
        if (!_isMounted.current) return;
        setCameraPosition(cPosition)
    }, [cPosition]);

    const pFov = props.fov;
    const [fov, setFOV] = useState(props.fov);
    useEffect(() => {
        if (!_isMounted.current) return;
        setFOV(pFov)
    }, [pFov]);

    const canLoad = useMemo(() => {
        return props.canLoad === undefined || props.canLoad
    }, [props.canLoad]);

    const data = useMemo(() => {
        return  {
            fov: fov,
            floorRotation: floorRotation,

            positionX: cameraPosition[0],
            positionY: cameraPosition[1],
            positionZ: cameraPosition[2],

            rotationX: cameraRotation[0],
            rotationY: cameraRotation[1],
            rotationZ: cameraRotation[2],
        }
    }, [cameraPosition, cameraRotation, floorRotation, fov]);

    const datGuiUpdated = useCallback((data:any) => {
        if (!_isMounted.current) return;
        setFOV(data.fov);
        setFloorRotation(data.floorRotation);
        setCameraPosition([data.positionX, data.positionY, data.positionZ]);
        setCameraRotation([data.rotationX, data.rotationY, data.rotationZ]);
        return data
    }, []);

    const downloadProps = useCallback(() => {
        const jsonData = {
            "cameraPosition": cameraPosition,
            "cameraRotation": cameraRotation,
            "floorRotation": floorRotation,
            "fov": fov
        };

        const filename = "data.json";
        const file = new Blob([JSON.stringify(jsonData, null, 4)], {type: "text/json"});
        if (window.navigator.msSaveOrOpenBlob) // IE10+
            window.navigator.msSaveOrOpenBlob(file, filename);
        else { // Others
            const a = document.createElement("a"),
                url = URL.createObjectURL(file);
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            safelyTimeout(function() {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }, 0);
        }
    }, [cameraPosition, cameraRotation, floorRotation, fov]);

    const disposeAll = useCallback(() => {
        if (canvas.current) {
            canvas.current.width = canvas.current.height = 1;
            const parent = canvas.current.parentElement;
            if (parent) {
                parent.removeChild(canvas.current)
            }
        }
    }, []);

    const _disposeRef = useRef(disposeAll);
    useEffect(() => { _disposeRef.current = disposeAll; }, [disposeAll]);

    const _isMounted = useRef(true);
    const canvas = useRef<HTMLCanvasElement>();
    useLayoutEffect(() => {
        _isMounted.current = true;
        // Dispose renderer on unmount
        return () => {
            _isMounted.current = false;
            if (_disposeRef.current) {
                _disposeRef.current()
            }
        }
    }, []);

    const onCreate = useCallback((props:CanvasContext) => {
        if (!_isMounted.current) return;
        setContext(props);
        canvas.current = props.gl.domElement;

        //shadows
        props.gl.physicallyCorrectLights = true;
        props.gl.pixelRatio = window.devicePixelRatio;
        props.gl.shadowMap.enabled = true;
        props.gl.shadowMap.type = THREE.PCFSoftShadowMap;
        props.gl.shadowMap.autoUpdate = true;

    }, []);

    const initialize = useCallback(() => {
        //set up API
        cbapi.setMaterial = setMaterial;
        cbapi.setFloorRotation = setFloorRotation
    }, []);

    const initializeRef = useRef(initialize);
    useEffect(() => { initializeRef.current = initialize; }, [initialize]);

    useEffect(() => {
        if (initializeRef.current) {
            initializeRef.current()
        }
    }, []);

    return useMemo(() => (
        <div id={"CBVisualizer"} style={{ width: '100%', height: '100%' }}>
            <div id={"drawing-mark-overlay"} style={{
                display: 'none', userSelect: 'none', pointerEvents: 'none',
                position: 'fixed', top: '400px', left: '500px', borderRadius: '100%', zIndex: 1000
            }} />

            {canLoad && <Canvas shadowMap onCreated={onCreate} pixelRatio={window.devicePixelRatio} invalidateFrameloop camera={{
                position: new THREE.Vector3(cameraPosition[0], cameraPosition[1], cameraPosition[2]),
                rotation: new THREE.Euler(cameraRotation[0], cameraRotation[1], cameraRotation[2])
            }}>
                <CBSceneRenderer {...props}
                                 canLoad={canLoad}
                                 toolMode={props.toolMode === undefined ? CBToolMode.Select : props.toolMode}
                                 context={context}
                                 client={browserProperties}
                                 logo={logo}
                                 fov={fov}
                                 material={material || props.material}
                                 cameraPosition={cameraPosition}
                                 cameraRotation={cameraRotation}
                                 floorRotation={floorRotation} />
            </Canvas>}
            <span ref={logo} id={"legally-required-logo"} style={{position:'absolute', bottom:'15px', right:'20px', color:'#ddd', fontSize:'1.0vmax', userSelect:'none', opacity:0.8}}>
                powered by Cambrian
            </span>

            <WebClientInfo onClientStateChanged={setBrowserProperties} />

            {props.showControls && <DatGui data={data} onUpdate={datGuiUpdated} labelWidth={15}>
                <DatFolder title={"Click Here to collapse"} closed={false}>
                    <DatButton label={"Click Here to Download json data"} onClick={downloadProps} />
                    <DatFolder title={"General Parameters"} closed={false}>
                        <DatBoolean path='useSizingTexture' label='Use Sizing Texture?' />
                        <DatNumber path='fov' label='FOV' min={20} max={120} step={0.1} />
                        <DatNumber path='floorRotation' label='Floor' min={-3.14} max={3.14} step={0.01} />
                    </DatFolder>

                    <DatFolder title={"Camera Position"} closed={false}>
                        <DatNumber path='positionX' label='X' min={0} max={3} step={0.01} />
                        <DatNumber path='positionY' label='Y' min={0} max={3} step={0.01} />
                        <DatNumber path='positionZ' label='Z' min={0} max={3} step={0.01} />
                    </DatFolder>

                    <DatFolder title={"Camera Rotation"} closed={false}>
                        <DatNumber path='rotationX' label='X' min={-1.57} max={1.57} step={0.001} />
                        <DatNumber path='rotationY' label='Y' min={-1.57} max={1.57} step={0.001} />
                        <DatNumber path='rotationZ' label='Z' min={-1.57} max={1.57} step={0.001} />
                    </DatFolder>
                </DatFolder>
            </DatGui>}
        </div>
    ), [cameraPosition, canLoad, cameraRotation, context, datGuiUpdated, data, downloadProps, floorRotation, fov, logo, onCreate, props])
}